import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Pencil, History, Trash2 } from "lucide-react";
import { StatusBadge } from "../StatusBadge";
import { LoadingSpinner } from "../LoadingSpinner";
import { ErrorState } from "../ErrorState";
import { EmptyState } from "../EmptyState";
import { Modal } from "../Modal";
import {
  listEnrolments,
  exportEnrolmentsUrl,
  sendWhatsapp,
  updateParent,
  updateStudent,
  removeParentFromEvent,
  removeStudentFromEvent,
  getWhatsappSendLog,
} from "../../services/enrolment.service";
import { getApiErrorMessage } from "../../lib/apiClient";
import { ConfirmDialog } from "../ConfirmDialog";
import type { EnrolmentRow } from "../../types";

interface ParentGroup {
  parent: EnrolmentRow["parent"];
  rows: EnrolmentRow[];
}

function groupByParent(rows: EnrolmentRow[]): ParentGroup[] {
  const groups = new Map<string, ParentGroup>();
  for (const row of rows) {
    const existing = groups.get(row.parent.id);
    if (existing) {
      existing.rows.push(row);
    } else {
      groups.set(row.parent.id, { parent: row.parent, rows: [row] });
    }
  }
  return Array.from(groups.values());
}

function EditParentModal({
  eventId,
  parent,
  onClose,
}: {
  eventId: string;
  parent: EnrolmentRow["parent"] | null;
  onClose: () => void;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (parent) {
      setName(parent.name);
      setPhone(parent.phone);
    }
  }, [parent]);

  const mutation = useMutation({
    mutationFn: () => updateParent(eventId, parent!.id, { name, phone }),
    onSuccess: () => {
      toast.success("Parent updated");
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "enrolments"] });
      onClose();
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={!!parent}
      onClose={onClose}
      title="Edit Parent"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="edit-parent-form" className="btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : "Save"}
          </button>
        </>
      }
    >
      <form id="edit-parent-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="parent-name">
            Name
          </label>
          <input id="parent-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="label" htmlFor="parent-phone">
            Phone
          </label>
          <input id="parent-phone" className="input" value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </div>
      </form>
    </Modal>
  );
}

function EditStudentModal({
  eventId,
  student,
  onClose,
}: {
  eventId: string;
  student: EnrolmentRow["student"] | null;
  onClose: () => void;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [icNumber, setIcNumber] = useState("");
  const [age, setAge] = useState("");

  useEffect(() => {
    if (student) {
      setName(student.name);
      setIcNumber(student.icNumber);
      setAge(String(student.age));
    }
  }, [student]);

  const mutation = useMutation({
    mutationFn: () => updateStudent(eventId, student!.id, { name, icNumber, age: Number(age) }),
    onSuccess: () => {
      toast.success("Student updated");
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "enrolments"] });
      onClose();
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={!!student}
      onClose={onClose}
      title="Edit Student"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="edit-student-form" className="btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : "Save"}
          </button>
        </>
      }
    >
      <form id="edit-student-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="student-name">
            Name
          </label>
          <input id="student-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="label" htmlFor="student-ic">
            IC number
          </label>
          <input id="student-ic" className="input" value={icNumber} onChange={(e) => setIcNumber(e.target.value)} required />
        </div>
        <div>
          <label className="label" htmlFor="student-age">
            Age
          </label>
          <input
            id="student-age"
            type="number"
            min={0}
            max={120}
            className="input"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            required
          />
        </div>
      </form>
    </Modal>
  );
}

function WhatsappHistoryModal({
  eventId,
  parent,
  onClose,
}: {
  eventId: string;
  parent: EnrolmentRow["parent"] | null;
  onClose: () => void;
}): JSX.Element {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["events", eventId, "whatsapp-log", parent?.id],
    queryFn: () => getWhatsappSendLog(eventId, parent!.id),
    enabled: !!parent,
  });

  return (
    <Modal open={!!parent} onClose={onClose} title={`WhatsApp history — ${parent?.name ?? ""}`}>
      {isLoading && <LoadingSpinner />}
      {isError && <ErrorState message={getApiErrorMessage(error)} />}
      {data && data.length === 0 && (
        <EmptyState title="No WhatsApp messages sent yet" description="Send history will appear here once a message is sent." />
      )}
      {data && data.length > 0 && (
        <ul className="space-y-3">
          {data.map((entry) => (
            <li key={entry.id} className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{entry.sentBy.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(entry.createdAt).toLocaleString()}</p>
              </div>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{entry.sentBy.email}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Children: {entry.studentNames}</p>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">
        A record here only means a staff member clicked Send WhatsApp — it does not confirm delivery or that the
        parent read the message.
      </p>
    </Modal>
  );
}

export function EnrolmentTable({ eventId }: { eventId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [editingParent, setEditingParent] = useState<EnrolmentRow["parent"] | null>(null);
  const [editingStudent, setEditingStudent] = useState<EnrolmentRow["student"] | null>(null);
  const [historyParent, setHistoryParent] = useState<EnrolmentRow["parent"] | null>(null);
  const [removeParentTarget, setRemoveParentTarget] = useState<ParentGroup | null>(null);
  const [removeStudentTarget, setRemoveStudentTarget] = useState<EnrolmentRow | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["events", eventId, "enrolments", { search, status, whatsapp }],
    queryFn: () => listEnrolments(eventId, { search, status, whatsapp }),
  });

  const groups = useMemo(() => (data ? groupByParent(data) : []), [data]);

  const whatsappMutation = useMutation({
    mutationFn: async ({ enrolmentId, pendingTab }: { enrolmentId: string; pendingTab: Window | null }) => {
      const result = await sendWhatsapp(eventId, enrolmentId);
      return { result, pendingTab };
    },
    onSuccess: ({ result, pendingTab }) => {
      // The tab was already opened synchronously inside the button's onClick
      // (see below) so it counts as a real user gesture on iOS Safari, which
      // otherwise silently blocks any window.open()/navigation triggered
      // after an await — exactly the "toast says sent but nothing opens"
      // symptom. We just point that already-open tab at the real link once
      // we have it. Falls back to opening fresh if the pre-open failed
      // (e.g. a popup blocker), same as before on browsers that allow it.
      if (pendingTab && !pendingTab.closed) {
        pendingTab.location.href = result.waLink;
      } else {
        window.open(result.waLink, "_blank", "noopener,noreferrer");
      }

      queryClient.invalidateQueries({ queryKey: ["events", eventId, "enrolments"] });
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "whatsapp-log"] });
      toast.success("WhatsApp link opened and send recorded for all children");
    },
    onError: (err, variables) => {
      variables.pendingTab?.close();
      toast.error(getApiErrorMessage(err));
    },
  });

  const removeParentMutation = useMutation({
    mutationFn: () => removeParentFromEvent(eventId, removeParentTarget!.parent.id),
    onSuccess: () => {
      toast.success("Parent removed from event");
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "enrolments"] });
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "dashboard"] });
      setRemoveParentTarget(null);
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const removeStudentMutation = useMutation({
    mutationFn: () => removeStudentFromEvent(eventId, removeStudentTarget!.student.id),
    onSuccess: () => {
      toast.success("Child removed from event");
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "enrolments"] });
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "dashboard"] });
      setRemoveStudentTarget(null);
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row">
          <input
            className="input sm:max-w-xs"
            placeholder="Search parent, student, phone, IC..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className="input sm:max-w-[160px]" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="DECLINED">Declined</option>
          </select>
          <select className="input sm:max-w-[180px]" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}>
            <option value="">All WhatsApp</option>
            <option value="sent">Sent</option>
            <option value="not_sent">Not sent</option>
          </select>
        </div>
        <a href={exportEnrolmentsUrl(eventId)} className="btn-secondary whitespace-nowrap">
          Export CSV
        </a>
      </div>

      {isLoading && <LoadingSpinner />}
      {isError && <ErrorState message={getApiErrorMessage(error)} />}
      {data && data.length === 0 && <EmptyState title="No enrolments found" description="Try adjusting your search or filters, or import students." />}

      {groups.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead className="bg-slate-50 dark:bg-slate-700/60">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Parent</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Children</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">WhatsApp</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {groups.map((group) => {
                // A single WhatsApp send covers the whole parent — the server
                // fans it out to every sibling enrolment for this event, so
                // any one enrolment id in the group works as the request target.
                const primaryEnrolmentId = group.rows[0].id;
                const totalSendCount = Math.max(...group.rows.map((r) => r.whatsappSendCount));

                return (
                  <tr key={group.parent.id} className="align-top transition-colors duration-100 hover:bg-slate-100 dark:hover:bg-slate-700/60">
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-1.5">
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{group.parent.name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{group.parent.phone}</p>
                        </div>
                        <button
                          type="button"
                          className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-600 dark:hover:text-slate-200"
                          onClick={() => setEditingParent(group.parent)}
                          aria-label={`Edit ${group.parent.name}`}
                          title="Edit parent"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          className="rounded p-1 text-slate-400 transition-colors hover:bg-red-100 hover:text-red-600 dark:text-slate-500 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                          onClick={() => setRemoveParentTarget(group)}
                          aria-label={`Remove ${group.parent.name} from event`}
                          title="Remove parent and all children from event"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <ul className="space-y-1.5">
                        {group.rows.map((row) => (
                          <li key={row.id} className="flex flex-wrap items-center gap-1.5">
                            <StatusBadge status={row.status} />
                            <span className="text-sm text-slate-900 dark:text-slate-100">{row.student.name}</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              (IC {row.student.icNumber}, age {row.student.age})
                            </span>
                            {row.status === "DECLINED" && row.declineReason && (
                              <span
                                className="text-xs italic text-slate-400 dark:text-slate-500"
                                title={row.declineReason}
                              >
                                — "{row.declineReason}"
                              </span>
                            )}
                            <button
                              type="button"
                              className="rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-600 dark:hover:text-slate-200"
                              onClick={() => setEditingStudent(row.student)}
                              aria-label={`Edit ${row.student.name}`}
                              title="Edit student"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              className="rounded p-0.5 text-slate-400 transition-colors hover:bg-red-100 hover:text-red-600 dark:text-slate-500 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                              onClick={() => setRemoveStudentTarget(row)}
                              aria-label={`Remove ${row.student.name} from event`}
                              title="Remove child from event"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-1.5 text-xs text-slate-400 dark:text-slate-500">
                        {group.rows.map((r) => r.student.name).join(", ")}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {totalSendCount > 0 ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-sm text-slate-600 underline decoration-dotted transition-colors hover:text-brand-600 dark:text-slate-300 dark:hover:text-brand-400"
                          onClick={() => setHistoryParent(group.parent)}
                          title="This only means staff initiated the WhatsApp message — it does not confirm delivery or that the parent read it. Click to view send history."
                        >
                          <History className="h-3.5 w-3.5" />
                          Sent {totalSendCount} time{totalSendCount > 1 ? "s" : ""}
                        </button>
                      ) : (
                        <span className="text-sm text-slate-600 dark:text-slate-300">Not Sent</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="text-sm font-medium text-brand-600 hover:text-brand-800 disabled:opacity-50 dark:text-brand-400 dark:hover:text-brand-300 transition-colors duration-150"
                        disabled={whatsappMutation.isPending}
                        onClick={() => {
                          // Must call window.open() synchronously within the
                          // click handler, before any await, or iOS Safari
                          // won't treat the later navigation as user-initiated.
                          // Deliberately omitting "noopener" here: we need the
                          // returned window reference to navigate it once the
                          // real wa.me link is known (see whatsappMutation).
                          const pendingTab = window.open("about:blank", "_blank");
                          whatsappMutation.mutate({ enrolmentId: primaryEnrolmentId, pendingTab });
                        }}
                        title={
                          group.rows.length > 1
                            ? `Sends one message covering all ${group.rows.length} children`
                            : "Send WhatsApp confirmation"
                        }
                      >
                        Send WhatsApp{group.rows.length > 1 ? ` (${group.rows.length})` : ""}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <EditParentModal eventId={eventId} parent={editingParent} onClose={() => setEditingParent(null)} />
      <EditStudentModal eventId={eventId} student={editingStudent} onClose={() => setEditingStudent(null)} />
      <WhatsappHistoryModal eventId={eventId} parent={historyParent} onClose={() => setHistoryParent(null)} />
      <ConfirmDialog
        open={!!removeParentTarget}
        onCancel={() => setRemoveParentTarget(null)}
        onConfirm={() => removeParentMutation.mutate()}
        isLoading={removeParentMutation.isPending}
        title="Remove parent from event?"
        message={`This will remove ${removeParentTarget?.parent.name ?? "this parent"} and all ${removeParentTarget?.rows.length ?? 0} children from this event. Their profiles will be kept.`}
        confirmLabel="Remove parent"
        danger
      />
      <ConfirmDialog
        open={!!removeStudentTarget}
        onCancel={() => setRemoveStudentTarget(null)}
        onConfirm={() => removeStudentMutation.mutate()}
        isLoading={removeStudentMutation.isPending}
        title="Remove child from event?"
        message={`This will remove ${removeStudentTarget?.student.name ?? "this child"} from this event. Their profile will be kept.`}
        confirmLabel="Remove child"
        danger
      />
    </div>
  );
}
